import React, { useState, useEffect, useRef } from 'react';
import { 
  Menu, X, Phone, Mail, MapPin, CheckCircle, 
  Wrench, Shield, Clock, ChevronRight, User, 
  LogOut, Camera, FileText, Settings, Car,
  Users, Trash2, Plus, Upload, Image as ImageIcon, Loader, Filter,
  Search, AlertTriangle, Calendar, ArrowLeft, Building, Edit, Save,
  Inbox, FileImage, Layers, ChevronDown, ArrowUpDown, FileCheck,
  QrCode
} from 'lucide-react';

// --- Firebase Imports ---
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, collection, addDoc, onSnapshot, 
  deleteDoc, doc, updateDoc
} from 'firebase/firestore';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// --- CSS/Tailwind Injection ---
if (typeof document !== 'undefined' && !document.getElementById('tailwind-cdn')) {
  const script = document.createElement('script');
  script.id = 'tailwind-cdn';
  script.src = 'https://cdn.tailwindcss.com';
  document.head.appendChild(script);
}

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyC85lGg6aBrZpePtPl2aWlLyBqDAVnGHLc",
  authDomain: "ks24h-9d9ae.firebaseapp.com",
  projectId: "ks24h-9d9ae",
  storageBucket: "ks24h-9d9ae.firebasestorage.app",
  messagingSenderId: "708371916704",
  appId: "1:708371916704:web:907356cb656976506966c5",
  measurementId: "G-YRYVBN4438"
};

let app, auth, db, storage;
try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app); 
} catch (error) {
  console.error("Firebase 初始化發生錯誤:", error);
}

const LOGO_SRC = process.env.PUBLIC_URL + '/logo.jpg'; 
const HERO_BG_SRC = process.env.PUBLIC_URL + '/hero-bg.jpg'; 
const ABOUT_IMG_SRC = process.env.PUBLIC_URL + '/ks1150402.jpg';

// 🟢 總管理員帳號：ks24h，密碼：ksks911118
const SEED_USERS = [
  { username: 'ks24h', password: 'ksks911118', name: '總管理員', role: 'admin' },
  { username: 'staff01', password: 'staff123', name: '維修部阿豪', role: 'staff' }
];

// --- 圖片壓縮工具 ---
const compressImageToBlob = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl); 
      const canvas = document.createElement('canvas');
      const MAX_WIDTH = 1200; 
      let width = img.width;
      let height = img.height;
      if (width > MAX_WIDTH) { height *= MAX_WIDTH / width; width = MAX_WIDTH; }
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.7); 
    };
    img.onerror = (error) => { URL.revokeObjectURL(objectUrl); reject(error); };
    img.src = objectUrl;
  });
};

// ==========================================
//           子組件定義 
// ==========================================

function CompanyLogo() {
  return (
    <img src={LOGO_SRC} alt="高陞停車場設備有限公司 LOGO" className="h-12 w-12 rounded-full shadow-md object-cover bg-white" />
  );
}

function NavButton({ children, onClick }) { 
  return <button onClick={onClick} className="font-medium px-3 py-2 rounded-md hover:text-white hover:bg-blue-800 transition">{children}</button>; 
}

function MobileNavButton({ children, onClick }) { 
  return <button onClick={onClick} className="block w-full text-left px-4 py-4 text-white font-medium hover:bg-blue-700 border-b border-blue-800">{children}</button>; 
}

function FeatureCard({ icon, title, desc }) { 
  return (
    <div className="text-center p-8 border rounded-2xl hover:-translate-y-2 hover:shadow-xl transition bg-gray-50 group">
      <div className="flex justify-center mb-6">
        <div className="bg-white p-4 rounded-2xl shadow-sm group-hover:bg-blue-600 transition [&>svg]:group-hover:text-white">{icon}</div>
      </div>
      <h4 className="text-xl font-bold mb-4">{title}</h4>
      <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
    </div>
  ); 
}

function ContactForm({ onSubmit }) {
  const [formData, setFormData] = useState({ name: '', phone: '', community: '', type: '定期保養詢價', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); 

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const res = await onSubmit(formData);
    setIsSubmitting(false);
    if (res.ok) { 
      setSubmitStatus('success'); 
      setFormData({ name: '', phone: '', community: '', type: '定期保養詢價', message: '' }); 
    } else { 
      window.alert("送出失敗：" + res.error); 
    }
  };

  if (submitStatus === 'success') return (
    <div className="bg-green-50 text-green-800 p-8 rounded-xl text-center">
      <CheckCircle className="inline-block mb-4 h-12 w-12 text-green-500" />
      <h4 className="text-xl font-bold mb-2">表單已成功送出！</h4>
      <p className="text-gray-600 text-sm">我們會盡快與您聯絡，謝謝！</p>
      <button onClick={() => setSubmitStatus(null)} className="mt-6 text-green-600 font-bold hover:underline">再填寫一筆</button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div><label className="block text-sm font-bold text-gray-700 mb-1">姓名</label><input type="text" required className="w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-blue-500" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} /></div>
        <div><label className="block text-sm font-bold text-gray-700 mb-1">電話</label><input type="tel" required className="w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-blue-500" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} /></div>
      </div>
      <div><label className="block text-sm font-bold text-gray-700 mb-1">社區名稱</label><input type="text" required className="w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-blue-500" value={formData.community} onChange={e => setFormData({...formData, community: e.target.value})} /></div>
      <div>
        <label className="block text-sm font-bold text-gray-700 mb-1">需求類型</label>
        <select className="w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-blue-500" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}>
          <option>定期保養詢價</option><option>單次緊急維修</option><option>零件更換報價</option><option>其他問題</option>
        </select>
      </div>
      <div><label className="block text-sm font-bold text-gray-700 mb-1">詳細說明</label><textarea rows="3" className="w-full px-4 py-3 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-blue-500" value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} /></div>
      <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold transition duration-200">{isSubmitting ? '送出中...' : '送出表單'}</button>
    </form>
  );
}

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!onLogin(username, password)) setError('帳號或密碼錯誤。');
  };

  return (
    <div className="min-h-[600px] flex items-center justify-center bg-gray-100 px-4 py-12">
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100">
        <div className="text-center mb-8">
          <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-100"><User className="w-10 h-10 text-blue-600" /></div>
          <h2 className="text-2xl font-bold text-gray-800">系統管理登入</h2>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center font-bold">{error}</div>}
          <div><label className="block text-sm font-bold text-gray-700 mb-1">帳號</label><input type="text" value={username} onChange={e => setUsername(e.target.value)} className="w-full px-4 py-3 bg-gray-50 border rounded-xl outline-none focus:ring-2 focus:ring-blue-500" required /></div>
          <div><label className="block text-sm font-bold text-gray-700 mb-1">密碼</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 bg-gray-50 border rounded-xl outline-none focus:ring-2 focus:ring-blue-500" required /></div>
          <button type="submit" className="w-full bg-blue-900 hover:bg-blue-800 text-white py-3.5 rounded-xl font-bold transition duration-200">登入系統</button>
        </form>
      </div>
    </div>
  );
}

function RemittancePage({ onSubmit }) {
  const [formData, setFormData] = useState({ community: '', parkingSpace: '', phone: '', amount: '', accountLast5: '', quoteNumber: '' });
  const [quoteFile, setQuoteFile] = useState(null); 
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const res = await onSubmit(formData, quoteFile);
    setIsSubmitting(false);
    
    if (res.ok) { 
      setSubmitStatus('success'); 
      setFormData({ community: '', parkingSpace: '', phone: '', amount: '', accountLast5: '', quoteNumber: '' }); 
      setQuoteFile(null);
    } else { 
      let errorMsg = res.error;
      if (errorMsg && errorMsg.includes('Missing or insufficient permissions')) {
        errorMsg = "權限不足 (安全規則可能還在更新中，請等待 3~5 分鐘後再試一次！)";
      }
      window.alert("抱歉，無法送出！\n\n系統訊息：" + errorMsg);
    }
  };

  return (
    <div className="min-h-[85vh] bg-gradient-to-b from-blue-50 to-white py-12 px-4 flex items-center justify-center">
      <div className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white p-10 text-center relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/5 rounded-full blur-xl"></div>
          <FileCheck size={56} className="mx-auto mb-4 text-orange-400 drop-shadow" />
          <h2 className="text-3xl md:text-4xl font-extrabold mb-3 tracking-wide">同意報價與匯款回報</h2>
          <p className="text-blue-100/95 max-w-md mx-auto text-sm md:text-base leading-relaxed">
            感謝您的支持，請填寫以下資訊以便我們為您加速對帳與安排作業。
          </p>
        </div>
        
        {submitStatus === 'success' ? (
          <div className="p-12 text-center">
            <div className="inline-flex items-center justify-center bg-green-50 p-4 rounded-full mb-6">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <h3 className="text-2xl md:text-3xl font-black text-gray-800 mb-3">資料已成功送出！</h3>
            <p className="text-gray-600 mb-8 text-base md:text-lg leading-relaxed max-w-md mx-auto">
              我們已收到您的回報與相關附件，會計人員將盡快為您核對，並於對帳無誤後主動與您聯絡安排後續行程。
            </p>
            <button onClick={() => setSubmitStatus(null)} className="text-blue-600 font-bold hover:text-blue-800 hover:underline text-base">返回重新填寫</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-8 md:p-10 space-y-8">
            
            {/* 1. 社區名稱 */}
            <div>
              <label className="block text-base font-black text-gray-800 mb-2 flex items-center justify-between">
                <span>1. 社區大樓名稱 <span className="text-red-500 font-bold">*</span></span>
                <span className="text-xs font-normal text-gray-400">請填寫完整的社區名稱</span>
              </label>
              <input 
                type="text" 
                required 
                placeholder="例如：高陞大廈、新竹皇家社區" 
                className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:bg-white focus:ring-4 focus:ring-blue-100 outline-none transition text-base font-medium placeholder-gray-400 shadow-sm" 
                value={formData.community} 
                onChange={e => setFormData({...formData, community: e.target.value})} 
              />
            </div>

            {/* 2. 車位號碼 & 3. 聯絡電話 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-base font-black text-gray-800 mb-2 flex items-center justify-between">
                  <span>2. 車位號碼 <span className="text-red-500 font-bold">*</span></span>
                  <span className="text-xs font-normal text-gray-400">請輸入車位編號</span>
                </label>
                <input 
                  type="text" 
                  required 
                  placeholder="例如：B2-15 號車位" 
                  className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:bg-white focus:ring-4 focus:ring-blue-100 outline-none transition text-base font-medium placeholder-gray-400 shadow-sm" 
                  value={formData.parkingSpace} 
                  onChange={e => setFormData({...formData, parkingSpace: e.target.value})} 
                />
              </div>
              <div>
                <label className="block text-base font-black text-gray-800 mb-2 flex items-center justify-between">
                  <span>3. 聯絡電話 <span className="text-red-500 font-bold">*</span></span>
                  <span className="text-xs font-normal text-gray-400">方便對帳聯絡的號碼</span>
                </label>
                <input 
                  type="tel" 
                  required 
                  placeholder="請輸入手機或市話" 
                  className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:bg-white focus:ring-4 focus:ring-blue-100 outline-none transition text-base font-medium placeholder-gray-400 shadow-sm" 
                  value={formData.phone} 
                  onChange={e => setFormData({...formData, phone: e.target.value})} 
                />
              </div>
            </div>

            {/* 4. 匯款金額 & 5. 帳號末五碼 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-base font-black text-gray-800 mb-2 flex items-center justify-between">
                  <span>4. 匯款總金額</span>
                  <span className="text-xs font-normal text-gray-400">若尚未匯款可留空</span>
                </label>
                <div className="relative">
                  <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-base">NT$</span>
                  <input 
                    type="number" 
                    placeholder="請輸入金額 (例: 1500)" 
                    className="w-full pl-14 pr-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:bg-white focus:ring-4 focus:ring-blue-100 outline-none transition text-base font-medium placeholder-gray-400 shadow-sm" 
                    value={formData.amount} 
                    onChange={e => setFormData({...formData, amount: e.target.value})} 
                  />
                </div>
              </div>
              <div>
                <label className="block text-base font-black text-gray-800 mb-2 flex items-center justify-between">
                  <span>5. 匯款帳號後五碼</span>
                  <span className="text-xs font-normal text-gray-400">提款卡或網銀末5碼</span>
                </label>
                <input 
                  type="text" 
                  maxLength="5" 
                  placeholder="例如：12345" 
                  className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:bg-white focus:ring-4 focus:ring-blue-100 outline-none transition text-base font-medium placeholder-gray-400 shadow-sm font-mono tracking-widest" 
                  value={formData.accountLast5} 
                  onChange={e => setFormData({...formData, accountLast5: e.target.value})} 
                />
              </div>
            </div>

            {/* 6. 報價單單號 */}
            <div>
              <label className="block text-base font-black text-gray-800 mb-2 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                <span>6. 報價單單號 <span className="text-red-500 font-bold">*</span></span>
                <span className="text-xs font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded border border-red-100">
                  ※ 提示：請查看您收到的紙本或 PDF 報價單【右下角】
                </span>
              </label>
              <input 
                type="text" 
                required 
                placeholder="例如：01234" 
                className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:bg-white focus:ring-4 focus:ring-blue-100 outline-none transition text-base font-medium placeholder-gray-400 shadow-sm" 
                value={formData.quoteNumber} 
                onChange={e => setFormData({...formData, quoteNumber: e.target.value})} 
              />
            </div>

            {/* 7. 上傳已同意報價單 */}
            <div className="pt-2">
              <div className="block text-base font-black text-gray-800 mb-2 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                <span>7. 上傳已同意報價單</span>
                <span className="text-xs font-normal text-gray-400">支援拍照、圖檔或 PDF 檔</span>
              </div>
              <label className="mt-2 flex justify-center px-4 py-8 border-2 border-blue-200 border-dashed rounded-xl hover:bg-blue-50 transition relative bg-gray-50 cursor-pointer w-full group">
                <div className="space-y-3 text-center w-full">
                  <Upload className="mx-auto h-10 w-10 text-blue-400 group-hover:text-blue-500 transition" />
                  <div className="flex text-sm text-gray-600 justify-center w-full">
                    <span className="font-bold text-blue-600 group-hover:text-blue-500 transition">
                      {quoteFile ? quoteFile.name : '點擊整個框框選擇檔案或拍照'}
                    </span>
                    <input 
                      type="file" 
                      accept="image/*,.pdf" 
                      className="sr-only" 
                      onChange={(e) => {
                        if(e.target.files[0]) setQuoteFile(e.target.files[0]);
                      }} 
                    />
                  </div>
                </div>
              </label>
              {quoteFile && (
                <div className="text-center mt-3">
                  <button 
                    type="button" 
                    onClick={(e) => { e.preventDefault(); setQuoteFile(null); }} 
                    className="text-sm text-red-500 hover:text-red-700 hover:underline inline-block bg-red-50 hover:bg-red-100 px-4 py-1.5 rounded-full transition font-bold"
                  >
                    移除並重新選擇
                  </button>
                </div>
              )}
            </div>
            
            <div className="pt-6">
              <button 
                type="submit" 
                disabled={isSubmitting} 
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-bold hover:from-blue-700 hover:to-indigo-700 transition shadow-lg text-lg flex items-center justify-center space-x-2 active:scale-[0.99] disabled:opacity-50"
              >
                <span>{isSubmitting ? '對帳資料與附件安全傳送中...' : '確認送出回報'}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function AddRecordForm({ users, onAdd, onCancel, initialData, isEditing = false }) {
  const [formData, setFormData] = useState({
    userId: '', date: new Date().toISOString().split('T')[0], type: '定期保養', technician: '', itemsStr: '',
  });
  
  const [docs, setDocs] = useState([]);
  const [photos, setPhotos] = useState([]);
  
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);

  const fileInputDocRef = useRef(null);
  const fileInputPhotoRef = useRef(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        userId: initialData.userId, date: initialData.date, type: initialData.type, technician: initialData.technician, itemsStr: initialData.items ? initialData.items.join(', ') : '',
      });
      setDocs(initialData.documentUrls ? initialData.documentUrls.map(url => ({ url, isNew: false })) : []);
      const initialPhotos = initialData.photoUrls ? initialData.photoUrls : (initialData.photoUrl ? [initialData.photoUrl] : []);
      setPhotos(initialPhotos.map(url => ({ url, isNew: false })));
    }
  }, [initialData]);

  const handleMultiPhotoChange = async (e, type) => { 
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setIsCompressing(true);
    try {
      const newItems = await Promise.all(files.map(async file => {
        const blob = await compressImageToBlob(file);
        return { url: URL.createObjectURL(blob), file: blob, isNew: true };
      }));
      if (type === 'document') setDocs(prev => [...prev, ...newItems]);
      if (type === 'photo') setPhotos(prev => [...prev, ...newItems]);
    } catch (error) { window.alert("圖片處理失敗，請試著換一張圖片"); } 
    finally {
      setIsCompressing(false);
      if (type === 'document' && fileInputDocRef.current) fileInputDocRef.current.value = '';
      if (type === 'photo' && fileInputPhotoRef.current) fileInputPhotoRef.current.value = '';
    }
  };

  const removeImage = (type, index) => {
    if (type === 'document') {
      setDocs(prev => { const arr = [...prev]; if (arr[index].isNew) URL.revokeObjectURL(arr[index].url); arr.splice(index, 1); return arr; });
    } else {
      setPhotos(prev => { const arr = [...prev]; if (arr[index].isNew) URL.revokeObjectURL(arr[index].url); arr.splice(index, 1); return arr; });
    }
  };

  const uploadFilesToStorage = async (itemsArray, prefix) => {
    const finalUrls = [];
    let count = 1;
    const newItemsCount = itemsArray.filter(i => i.isNew).length;

    for (let item of itemsArray) {
      if (!item.isNew) { finalUrls.push(item.url); } 
      else {
        setUploadStatus(`上傳圖片中... (${count}/${newItemsCount})`);
        const fileName = `records/${prefix}_${Date.now()}_${Math.floor(Math.random()*1000)}.jpg`;
        const fileRef = ref(storage, fileName);
        await uploadBytes(fileRef, item.file);
        const downloadUrl = await getDownloadURL(fileRef);
        finalUrls.push(downloadUrl);
        count++;
      }
    }
    return finalUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.userId) return window.alert("請選擇客戶！");
    setIsSubmitting(true);
    try {
      setUploadStatus('準備上傳中...');
      const finalDocUrls = await uploadFilesToStorage(docs, 'doc');
      const finalPhotoUrls = await uploadFilesToStorage(photos, 'photo');

      const newRecord = {
        userId: formData.userId, date: formData.date, type: formData.type, technician: formData.technician,
        items: formData.itemsStr.split(',').map(s => s.trim()).filter(Boolean),
        status: '完成', documentUrls: finalDocUrls, photoUrls: finalPhotoUrls,
      };
      
      setUploadStatus('儲存資料中...');
      const success = await onAdd(newRecord);
      if (success) { if (!isEditing) window.alert('保養紀錄新增成功！'); onCancel(); }
    } catch (err) { window.alert("上傳失敗，請確認是否已開通 Storage 儲存庫並設定規則。\n錯誤訊息：" + err.message); } 
    finally { setIsSubmitting(false); setUploadStatus(''); }
  };

  const clientList = users.filter(u => u.role === 'client');
  const filteredClients = clientList.filter(u => u.name.toLowerCase().includes(clientSearchTerm.toLowerCase()));
  const selectedClient = clientList.find(u => u.id === formData.userId);

  return (
    <div className={`${isEditing ? 'p-6' : 'bg-white p-6 rounded-lg shadow-sm border border-gray-200'}`}>
      <h3 className="text-2xl font-black text-gray-800 mb-6 flex items-center border-b pb-4">
        {isEditing ? <Edit className="mr-3 text-blue-600" /> : <Plus className="mr-3 text-blue-600" />} {isEditing ? '編輯保養紀錄' : '新增保養紀錄'}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="bg-gray-50 p-5 rounded-xl border border-gray-100">
          <h4 className="font-bold text-gray-700 mb-4 flex items-center"><FileText size={18} className="mr-2"/> 基本資料</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-bold mb-1.5 text-gray-600">選擇客戶</label>
              <div className={`w-full border p-2.5 rounded-lg outline-none flex justify-between items-center cursor-pointer bg-white ${!formData.userId ? 'text-gray-400' : 'text-gray-900'} ${isClientDropdownOpen ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-200'}`} onClick={() => setIsClientDropdownOpen(!isClientDropdownOpen)}>
                <span className="truncate">{selectedClient ? selectedClient.name : '請選擇社區...'}</span><ChevronDown size={18} className="text-gray-400 flex-shrink-0" />
              </div>
              {isClientDropdownOpen && (
                <><div className="fixed inset-0 z-[90]" onClick={() => setIsClientDropdownOpen(false)}></div>
                  <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 flex flex-col">
                    <div className="p-2 border-b border-gray-100 bg-gray-50">
                      <div className="relative"><Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" /><input type="text" placeholder="輸入關鍵字搜尋..." className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" value={clientSearchTerm} onChange={(e) => setClientSearchTerm(e.target.value)} autoFocus /></div>
                    </div>
                    <div className="overflow-y-auto custom-scrollbar flex-grow">
                      {filteredClients.length > 0 ? (filteredClients.map(u => (<div key={u.id} className={`px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm transition ${formData.userId === u.id ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-700'}`} onClick={() => { setFormData({...formData, userId: u.id}); setIsClientDropdownOpen(false); setClientSearchTerm(''); }}>{u.name}</div>))) : (<div className="px-4 py-6 text-center text-sm text-gray-500">查無符合的社區</div>)}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div><label className="block text-sm font-bold mb-1.5 text-gray-600">日期</label><input type="date" required className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} /></div>
            <div><label className="block text-sm font-bold mb-1.5 text-gray-600">類型</label><select className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}><option>定期保養</option><option>緊急維修</option><option>零件更換</option></select></div>
            <div><label className="block text-sm font-bold mb-1.5 text-gray-600">維修技師</label><input type="text" required placeholder="例如：王小明" className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={formData.technician} onChange={e => setFormData({...formData, technician: e.target.value})} /></div>
            <div className="md:col-span-2"><label className="block text-sm font-bold mb-1.5 text-gray-600">施作項目 (用逗號分隔)</label><textarea required placeholder="例如：機油更換, 鍊條調整, 異音排除" className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" rows="2" value={formData.itemsStr} onChange={e => setFormData({...formData, itemsStr: e.target.value})} /></div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="border border-gray-200 p-5 rounded-xl">
            <h4 className="font-bold text-gray-700 mb-3 flex items-center justify-between"><span className="flex items-center"><FileImage size={18} className="mr-2 text-blue-500"/> (1) 實體保養單上傳</span><span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">{docs.length} 張</span></h4>
            {docs.length > 0 && (<div className="grid grid-cols-2 gap-2 mb-3 max-h-48 overflow-y-auto pr-1 custom-scrollbar">{docs.map((item, index) => (<div key={`doc-${index}`} className="relative group border rounded overflow-hidden"><img src={item.url} alt={`保養單 ${index+1}`} className="w-full h-24 object-cover" /><button type="button" onClick={() => removeImage('document', index)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-80 hover:opacity-100"><X size={14} /></button></div>))}</div>)}
            <label className="mt-2 flex justify-center px-4 py-6 border-2 border-blue-200 border-dashed rounded-lg hover:bg-blue-50 transition relative cursor-pointer w-full group">
              <div className="space-y-2 text-center w-full">
                <Upload className="mx-auto h-8 w-8 text-blue-400 group-hover:text-blue-500 transition" />
                <div className="flex text-sm text-gray-600 justify-center w-full">
                  <span className="font-bold text-blue-600 group-hover:text-blue-500 transition">
                    {isCompressing ? '壓縮處理中...' : '點擊整個框框選擇相簿或拍照'}
                  </span>
                  <input ref={fileInputDocRef} type="file" multiple accept="image/*" className="sr-only" onChange={(e) => handleMultiPhotoChange(e, 'document')} disabled={isCompressing}/>
                </div>
              </div>
            </label>
          </div>

          <div className="border border-gray-200 p-5 rounded-xl">
            <h4 className="font-bold text-gray-700 mb-3 flex items-center justify-between"><span className="flex items-center"><Camera size={18} className="mr-2 text-orange-500"/> (2) 現場施工照片上傳</span><span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">{photos.length} 張</span></h4>
            {photos.length > 0 && (<div className="grid grid-cols-2 gap-2 mb-3 max-h-48 overflow-y-auto pr-1 custom-scrollbar">{photos.map((item, index) => (<div key={`photo-${index}`} className="relative group border rounded overflow-hidden"><img src={item.url} alt={`照片 ${index+1}`} className="w-full h-24 object-cover" /><button type="button" onClick={() => removeImage('photo', index)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-80 hover:opacity-100"><X size={14} /></button></div>))}</div>)}
            <label className="mt-2 flex justify-center px-4 py-6 border-2 border-orange-200 border-dashed rounded-lg hover:bg-orange-50 transition relative cursor-pointer w-full group">
              <div className="space-y-2 text-center w-full">
                <ImageIcon className="mx-auto h-8 w-8 text-orange-400 group-hover:text-orange-500 transition" />
                <div className="flex text-sm text-gray-600 justify-center w-full">
                  <span className="font-bold text-orange-600 group-hover:text-orange-500 transition">
                    {isCompressing ? '壓縮處理中...' : '點擊整個框框選擇相簿或拍照'}
                  </span>
                  <input ref={fileInputPhotoRef} type="file" multiple accept="image/*" className="sr-only" onChange={(e) => handleMultiPhotoChange(e, 'photo')} disabled={isCompressing}/>
                </div>
              </div>
            </label>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
          <button type="button" onClick={onCancel} className="px-6 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-lg">取消返回</button>
          <button type="submit" disabled={isSubmitting || isCompressing} className={`px-6 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 flex items-center shadow-lg ${isSubmitting || isCompressing ? 'opacity-50 cursor-not-allowed' : ''}`}>
            {isSubmitting ? (uploadStatus || '儲存中...') : <><Save size={18} className="mr-2" /> {isEditing ? '儲存變更' : '確認新增紀錄'}</>}
          </button>
        </div>
      </form>
    </div>
  );
}

function UserManagement({ users, onAddUser, onUpdateUser, onDeleteUser }) {
  const [newUser, setNewUser] = useState({ name: '', username: '', password: '', role: 'client' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editTarget, setEditTarget] = useState(null); 
  const [searchTerm, setSearchTerm] = useState(''); 
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const handleSubmit = async (e) => {
    e.preventDefault(); setIsSubmitting(true); await onAddUser(newUser); 
    setNewUser({ name: '', username: '', password: '', role: 'client' }); setIsSubmitting(false); window.alert("帳號新增成功！");
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault(); if (!editTarget) return; setIsSubmitting(true);
    await onUpdateUser(editTarget.id, { name: editTarget.name, password: editTarget.password, username: editTarget.username, role: editTarget.role });
    setIsSubmitting(false); setEditTarget(null);
  };

  const handleSort = (key) => {
    let direction = 'asc'; if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const filteredUsers = users.filter(user => user.name.toLowerCase().includes(searchTerm.toLowerCase()) || user.username.toLowerCase().includes(searchTerm.toLowerCase()));
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aValue = a[sortConfig.key] || ''; const bValue = b[sortConfig.key] || '';
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center"><Plus className="mr-2 h-5 w-5" /> 新增客戶/管理員帳號</h3>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">社區/使用者名稱</label><input required type="text" className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" placeholder="例如：美好大廈" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} /></div>
          <div><label className="block text-sm font-medium mb-1">權限角色</label><select className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}><option value="client">一般客戶</option><option value="staff">維修員工</option><option value="admin">系統管理員</option></select></div>
          <div><label className="block text-sm font-medium mb-1">登入帳號</label><input required type="text" className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" placeholder="例如：happy01" value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} /></div>
          <div><label className="block text-sm font-medium mb-1">密碼</label><input required type="text" className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" placeholder="設定密碼" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} /></div>
          <div className="md:col-span-2 pt-2"><button type="submit" disabled={isSubmitting} className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition w-full md:w-auto">建立帳號</button></div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <h3 className="text-lg font-bold text-gray-800">現有帳號列表</h3>
          <div className="relative w-full sm:w-64"><Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" /><input type="text" placeholder="搜尋名稱或帳號..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-sm border-b border-gray-200">
                <th className="px-6 py-3 cursor-pointer hover:bg-gray-200 transition select-none" onClick={() => handleSort('name')}><div className="flex items-center">名稱 <ArrowUpDown size={14} className={`ml-1 ${sortConfig.key === 'name' ? 'text-blue-600' : 'text-gray-300'}`} /></div></th>
                <th className="px-6 py-3 cursor-pointer hover:bg-gray-200 transition select-none" onClick={() => handleSort('username')}><div className="flex items-center">登入帳號 <ArrowUpDown size={14} className={`ml-1 ${sortConfig.key === 'username' ? 'text-blue-600' : 'text-gray-300'}`} /></div></th>
                <th className="px-6 py-3">密碼</th>
                <th className="px-6 py-3 cursor-pointer hover:bg-gray-200 transition select-none" onClick={() => handleSort('role')}><div className="flex items-center">權限 <ArrowUpDown size={14} className={`ml-1 ${sortConfig.key === 'role' ? 'text-blue-600' : 'text-gray-300'}`} /></div></th>
                <th className="px-6 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedUsers.map(u => {
                const roleColor = u.role === 'admin' ? 'bg-purple-100 text-purple-700' : u.role === 'staff' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700';
                const roleText = u.role === 'admin' ? '管理員' : u.role === 'staff' ? '員工' : '客戶';
                return (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{u.name}</td>
                    <td className="px-6 py-4 text-gray-600 font-mono text-sm">{u.username}</td>
                    {/* 🟢 安全遮罩：總管理員為 ks24h */}
                    <td className="px-6 py-4 text-gray-400 font-mono text-sm">{u.username === 'ks24h' ? '******' : u.password}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 text-xs rounded ${roleColor}`}>{roleText}</span></td>
                    <td className="px-6 py-4 text-right">
                      {/* 🟢 禁止編輯/刪除總管理員 ks24h */}
                      {u.username !== 'ks24h' && (
                        <>
                          <button onClick={() => setEditTarget({...u})} className="text-blue-400 hover:text-blue-600 p-2 mr-1"><Edit size={18} /></button>
                          <button onClick={() => onDeleteUser(u.id)} className="text-red-400 hover:text-red-600 p-2"><Trash2 size={18} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {sortedUsers.length === 0 && (<tr><td colSpan="5" className="px-6 py-8 text-center text-gray-400">找不到符合的帳號</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {editTarget && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setEditTarget(null)}></div>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm relative z-10 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4"><Edit className="inline mr-2 h-5 w-5" /> 修改帳號資料</h3>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div><label className="block text-sm font-medium mb-1">名稱</label><input required type="text" className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500" value={editTarget.name} onChange={e => setEditTarget({...editTarget, name: e.target.value})} /></div>
              <div><label className="block text-sm font-medium mb-1">權限角色</label><select className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500" value={editTarget.role} onChange={e => setEditTarget({...editTarget, role: e.target.value})}><option value="client">一般客戶</option><option value="staff">維修員工</option><option value="admin">系統管理員</option></select></div>
              <div><label className="block text-sm font-medium mb-1">帳號</label><input required type="text" className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500" value={editTarget.username} onChange={e => setEditTarget({...editTarget, username: e.target.value})} /></div>
              <div><label className="block text-sm font-medium mb-1">密碼</label><input required type="text" className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500" value={editTarget.password} onChange={e => setEditTarget({...editTarget, password: e.target.value})} /></div>
              <div className="flex justify-end space-x-2 pt-2"><button type="button" onClick={() => setEditTarget(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">取消</button><button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">確認修改</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard({ 
  user, records, users, inquiries, remittances,
  onAddRecord, onUpdateRecord, 
  onAddUser, onUpdateUser, onDeleteUser, 
  onDeleteRecord, onViewDetail, onEditRecord,
  onUpdateInquiry, onDeleteInquiry,
  onUpdateRemittance, onDeleteRemittance 
}) {
  const [activeTab, setActiveTab] = useState('records');
  const [selectedCommunityId, setSelectedCommunityId] = useState(null); 
  const [searchTerm, setSearchTerm] = useState(''); 
  const [editInquiryTarget, setEditInquiryTarget] = useState(null);
  const [inquiryFormData, setInquiryFormData] = useState({});

  const handleOpenInquiryEdit = (inq) => {
    setEditInquiryTarget(inq);
    setInquiryFormData({ status: inq.status || '未處理', replyNote: inq.replyNote || '', name: inq.name || '', phone: inq.phone || '' });
  };

  const handleInquiryEditSubmit = async (e) => {
    e.preventDefault();
    await onUpdateInquiry(editInquiryTarget.id, inquiryFormData);
    setEditInquiryTarget(null);
  };

  const getDisplayedContent = () => {
    if (user.role === 'client') return { view: 'records', data: records.filter(r => r.userId === user.id) };
    if (!selectedCommunityId) return { view: 'communities', data: users.filter(u => u.role === 'client' && u.name.toLowerCase().includes(searchTerm.toLowerCase())) };
    return { view: 'records', data: records.filter(r => r.userId === selectedCommunityId) };
  };

  const { view, data } = getDisplayedContent();

  const roleBadgeColor = user.role === 'admin' ? 'bg-purple-100 text-purple-700' : user.role === 'staff' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700';
  const roleBadgeText = user.role === 'admin' ? '系統管理員' : user.role === 'staff' ? '維修部員工' : '簽約客戶';

  const renderRecordRow = (record) => {
    const typeColor = record.type === '定期保養' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
    return (
      <div key={record.id} onClick={() => onViewDetail(record)} className="group bg-white border-b border-gray-100 hover:bg-blue-50 transition cursor-pointer grid grid-cols-12 gap-4 py-4 px-4 items-center last:border-b-0">
        <div className="col-span-3 sm:col-span-2 text-sm text-gray-500 font-mono">{record.date}</div>
        <div className="col-span-5 sm:col-span-6 flex flex-col sm:flex-row sm:items-center gap-2">
          <span className={`inline-block px-2 py-0.5 text-xs rounded font-bold w-fit ${typeColor}`}>{record.type}</span>
          <span className="text-sm text-gray-600 truncate">{record.technician}</span>
        </div>
        <div className="hidden sm:flex col-span-2 items-center text-sm text-green-600 font-medium"><CheckCircle size={14} className="mr-1" /> {record.status}</div>
        <div className="col-span-4 sm:col-span-2 flex justify-end items-center">
          {user.role !== 'client' && (
            <>
              <button onClick={(e) => { e.stopPropagation(); onEditRecord(record); }} className="p-2 text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded-full transition mr-1" title="編輯"><Edit size={16} /></button>
              <button onClick={(e) => { e.stopPropagation(); onDeleteRecord(record.id); }} className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full transition mr-1" title="刪除"><Trash2 size={16} /></button>
            </>
          )}
          <ChevronRight size={18} className="text-gray-300 group-hover:text-blue-500" />
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">歡迎回來，{user.name}</h2>
          <span className={`text-xs px-2 py-1 rounded mt-1 inline-block ${roleBadgeColor}`}>
            {roleBadgeText}
          </span>
        </div>
        <button onClick={() => window.location.reload()} className="flex items-center text-gray-500 hover:text-red-500"><LogOut size={18} className="mr-1" /> 登出</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden sticky top-24">
            <button onClick={() => setActiveTab('records')} className={`w-full text-left px-6 py-4 border-l-4 font-medium flex items-center transition ${activeTab === 'records' ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-transparent hover:bg-gray-50'}`}><FileText size={18} className="mr-3" /> 保養/維修紀錄</button>
            {(user.role === 'admin' || user.role === 'staff') && (
               <button onClick={() => setActiveTab('remittances')} className={`w-full text-left px-6 py-4 border-l-4 font-medium flex items-center transition ${activeTab === 'remittances' ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-transparent hover:bg-gray-50'}`}><FileCheck size={18} className="mr-3" /> 報價與匯款回報</button>
            )}
            {user.role === 'admin' && <button onClick={() => setActiveTab('inquiries')} className={`w-full text-left px-6 py-4 border-l-4 font-medium flex items-center transition ${activeTab === 'inquiries' ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-transparent hover:bg-gray-50'}`}><Inbox size={18} className="mr-3" /> 線上諮詢留言</button>}
            {(user.role === 'admin' || user.role === 'staff') && <button onClick={() => setActiveTab('add')} className={`w-full text-left px-6 py-4 border-l-4 font-medium flex items-center transition ${activeTab === 'add' ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-transparent hover:bg-gray-50'}`}><Plus size={18} className="mr-3" /> 新增保養紀錄</button>}
            {user.role === 'admin' && <button onClick={() => setActiveTab('users')} className={`w-full text-left px-6 py-4 border-l-4 font-medium flex items-center transition ${activeTab === 'users' ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-transparent hover:bg-gray-50'}`}><Users size={18} className="mr-3" /> 社區/帳號管理</button>}
          </div>
        </div>

        <div className="lg:col-span-3">
          {activeTab === 'records' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-700 flex items-center">
                    {(user.role === 'admin' || user.role === 'staff') && selectedCommunityId ? (
                      <div className="flex items-center">
                        <button onClick={() => setSelectedCommunityId(null)} className="mr-3 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition"><ArrowLeft size={20} className="text-gray-600" /></button>
                        <span>{users.find(u => u.id === selectedCommunityId)?.name || '未知社區'} - 保養紀錄</span>
                      </div>
                    ) : (<>{user.role !== 'client' ? <Building className="mr-2" /> : <FileText className="mr-2" />}{user.role !== 'client' ? '選擇社區查看紀錄' : '歷史保養明細'}</>)}
                  </h3>
                </div>
                {(user.role === 'admin' || user.role === 'staff') && !selectedCommunityId && (
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="relative">
                      <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input type="text" placeholder="搜尋社區名稱..." className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              {view === 'communities' ? (
                data.length === 0 ? <div className="text-center py-10 bg-white rounded-lg text-gray-400 border border-dashed border-gray-300">查無符合的社區</div> : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.map(community => {
                      const count = records.filter(r => r.userId === community.id).length;
                      return (
                        <div key={community.id} onClick={() => { setSelectedCommunityId(community.id); setSearchTerm(''); }} className="bg-white p-6 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md cursor-pointer transition group">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center">
                              <div className="bg-blue-100 p-3 rounded-full mr-4 group-hover:bg-blue-600 transition duration-300"><Building size={24} className="text-blue-600 group-hover:text-white transition duration-300" /></div>
                              <div><h4 className="font-bold text-gray-800 text-lg">{community.name}</h4><p className="text-sm text-gray-500 font-mono mt-1">{community.username}</p></div>
                            </div>
                            <div className="text-right"><span className="block text-2xl font-bold text-blue-600">{count}</span><span className="text-xs text-gray-400">筆紀錄</span></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                data.length === 0 ? <div className="text-center py-10 bg-white rounded-lg text-gray-400 border border-dashed border-gray-300">尚無保養紀錄</div> : (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                      <div className="col-span-3 sm:col-span-2">日期</div><div className="col-span-5 sm:col-span-6">類型 / 技師</div><div className="hidden sm:block col-span-2">狀態</div><div className="col-span-4 sm:col-span-2 text-right">詳細</div>
                    </div>
                    {data.map(record => renderRecordRow(record))}
                  </div>
                )
              )}
            </div>
          )}

          {activeTab === 'remittances' && (user.role === 'admin' || user.role === 'staff') && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-700 mb-4 flex items-center"><FileCheck className="mr-2" /> 客戶報價回簽與匯款回報</h3>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-blue-900 mb-2 flex items-center">
                    <QrCode className="mr-2" /> 專屬回報 QR Code
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 max-w-md">
                    您可以將右側的 QR Code <strong className="text-gray-800">按右鍵「另存圖片」</strong>並印在實體報價單上。客戶用手機掃描後，就能免登入直接進入「報價回簽」專屬表單。
                  </p>
                  <div className="flex items-center space-x-2">
                    <input type="text" readOnly value="https://ks24h-9d9ae.web.app/#remittance" className="bg-gray-50 border border-gray-200 text-gray-500 text-sm rounded-lg px-3 py-2 w-full max-w-xs outline-none" />
                    <button 
                      onClick={() => { navigator.clipboard.writeText('https://ks24h-9d9ae.web.app/#remittance'); window.alert('專屬網址已複製！可以貼給客戶了。'); }} 
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm transition font-bold whitespace-nowrap"
                    >
                      複製網址
                    </button>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 shadow-inner flex-shrink-0">
                  <img 
                    src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://ks24h-9d9ae.web.app/%23remittance" 
                    alt="報價回簽 QR Code" 
                    className="w-32 h-32 md:w-40 md:h-40 rounded-lg object-contain bg-white p-2 border border-gray-100" 
                  />
                </div>
              </div>

              {remittances.length === 0 ? <div className="text-center py-10 bg-white rounded-lg text-gray-400 border border-dashed border-gray-300">目前沒有收到任何回報資料</div> : (
                <div className="space-y-4">
                  {remittances.map(remit => {
                    const isConfirmed = remit.status === '已確認';
                    const borderColor = isConfirmed ? 'border-l-4 border-l-green-500 opacity-70' : 'border-l-4 border-l-red-500';
                    const statusColor = isConfirmed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
                    const btnColor = isConfirmed ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-green-500 text-white hover:bg-green-600 shadow-md';

                    return (
                      <div key={remit.id} className={`bg-white p-6 rounded-lg shadow-sm border relative ${borderColor}`}>
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                          <div className="mb-4 md:mb-0">
                             <div className="flex items-center space-x-2 mb-2">
                                <span className={`px-2 py-1 text-xs rounded font-bold ${statusColor}`}>{remit.status || '未對帳'}</span>
                                <span className="text-xs text-gray-400">{remit.date}</span>
                             </div>
                             <h4 className="text-lg font-bold text-gray-900 mb-1">{remit.community} - <span className="text-blue-600">{remit.parkingSpace}</span> 車位</h4>
                             <p className="text-sm text-gray-600">聯絡電話：{remit.phone}</p>
                             
                             {/* 🟢 附件預覽按鈕 */}
                             {remit.fileUrl && (
                               <div className="mt-3">
                                 <a href={remit.fileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded hover:bg-indigo-100 transition text-sm font-medium">
                                   <FileImage size={16} className="mr-1.5" /> 檢視上傳的報價單 / 附件
                                 </a>
                               </div>
                             )}
                          </div>
                          
                          <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-sm md:text-right w-full md:w-auto">
                            <p className="mb-1"><span className="text-gray-500">報價單號：</span><strong className="text-gray-800 tracking-wider">{remit.quoteNumber}</strong></p>
                            <p className="mb-1">
                              <span className="text-gray-500">匯款金額：</span>
                              <strong className="text-gray-800 tracking-wider">{remit.amount ? `NT$ ${remit.amount}` : '未提供'}</strong>
                            </p>
                            <p>
                              <span className="text-gray-500">匯款後五碼：</span>
                              <strong className={`tracking-widest text-lg px-2 py-0.5 rounded ${remit.accountLast5 ? 'text-blue-600 bg-blue-50' : 'text-gray-400 bg-gray-100'}`}>
                                {remit.accountLast5 || '未提供'}
                              </strong>
                            </p>
                          </div>
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end space-x-3">
                           {user.role === 'admin' && (
                             <button onClick={() => onDeleteRemittance(remit.id)} className="text-gray-400 hover:text-red-500 flex items-center px-3 py-1.5 transition text-sm">
                               <Trash2 size={16} className="mr-1"/> 刪除
                             </button>
                           )}
                           <button 
                             onClick={() => onUpdateRemittance(remit.id, { status: isConfirmed ? '未對帳' : '已確認' })} 
                             className={`px-4 py-1.5 rounded-lg font-medium transition text-sm flex items-center ${btnColor}`}
                           >
                             <CheckCircle size={16} className="mr-1.5" /> {isConfirmed ? '取消確認 (切換回未對帳)' : '標記為：帳款已確認'}
                           </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'inquiries' && user.role === 'admin' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-700 mb-4 flex items-center"><Inbox className="mr-2" /> 線上諮詢紀錄</h3>
              {inquiries.length === 0 ? <div className="text-center py-10 bg-white rounded-lg text-gray-400 border border-dashed border-gray-300">目前沒有任何諮詢留言</div> : (
                <div className="space-y-4">
                  {inquiries.map(inq => {
                    const isReplied = inq.status === '已回覆';
                    const isProcessing = inq.status === '處理中';
                    const borderColor = isReplied ? 'border-l-4 border-l-green-500' : isProcessing ? 'border-l-4 border-l-yellow-500' : 'border-gray-200';
                    const statusColor = isReplied ? 'bg-green-100 text-green-800' : isProcessing ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';
                    const replyBorderColor = isReplied ? 'bg-green-50 border-green-100 text-green-800' : 'bg-yellow-50 border-yellow-100 text-yellow-800';
                    const replyTextColor = isReplied ? 'text-green-600' : 'text-yellow-600';

                    return (
                      <div key={inq.id} className={`bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition relative ${borderColor}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="flex items-center mb-2">
                              <span className={`inline-block px-2 py-1 text-xs rounded font-bold mr-2 ${statusColor}`}>{inq.status || '未處理'}</span>
                              <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded font-bold">{inq.type}</span>
                            </div>
                            <h4 className="font-bold text-gray-800 text-lg">{inq.name} <span className="text-sm font-normal text-gray-500 ml-2">{inq.phone}</span></h4>
                            <p className="text-sm text-gray-600 flex items-center mt-1"><Building size={14} className="mr-1 text-gray-400"/>{inq.community}</p>
                          </div>
                          <div className="text-right flex flex-col items-end">
                            <span className="text-xs text-gray-400 mb-3">{inq.date}</span>
                            <div className="flex space-x-2">
                              <button onClick={() => handleOpenInquiryEdit(inq)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition" title="編輯處理狀態"><Edit size={16} /></button>
                              <button onClick={() => onDeleteInquiry(inq.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition" title="刪除此紀錄"><Trash2 size={16} /></button>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-gray-700 text-sm mt-4 border border-gray-100">
                          <span className="text-xs text-gray-400 font-bold block mb-1 uppercase tracking-wider">客戶訊息：</span>
                          {inq.message || <span className="text-gray-400 italic">無詳細說明</span>}
                        </div>
                        {inq.replyNote && (
                          <div className={`mt-3 p-4 rounded-lg text-sm border ${replyBorderColor}`}>
                            <span className={`text-xs font-bold block mb-1 uppercase tracking-wider ${replyTextColor}`}>處理 / 回覆紀錄：</span>
                            <p>{inq.replyNote}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'add' && (user.role === 'admin' || user.role === 'staff') && (
            <AddRecordForm users={users} onAdd={onAddRecord} onCancel={() => setActiveTab('records')} />
          )}

          {activeTab === 'users' && user.role === 'admin' && (
            <UserManagement users={users} onAddUser={onAddUser} onUpdateUser={onUpdateUser} onDeleteUser={onDeleteUser} />
          )}
        </div>
      </div>

      {editInquiryTarget && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setEditInquiryTarget(null)}></div>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg relative z-10 p-6">
            <div className="flex justify-between items-center mb-6 border-b pb-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center"><Edit className="mr-2 h-5 w-5 text-blue-600" /> 編輯諮詢紀錄</h3>
              <button onClick={() => setEditInquiryTarget(null)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <form onSubmit={handleInquiryEditSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-bold text-gray-700 mb-1">聯絡人</label><input type="text" className="w-full border border-gray-200 p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50" value={inquiryFormData.name} onChange={e => setInquiryFormData({...inquiryFormData, name: e.target.value})} required /></div>
                <div><label className="block text-sm font-bold text-gray-700 mb-1">電話</label><input type="text" className="w-full border border-gray-200 p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50" value={inquiryFormData.phone} onChange={e => setInquiryFormData({...inquiryFormData, phone: e.target.value})} required /></div>
              </div>
              <div><label className="block text-sm font-bold text-gray-700 mb-1">目前處理狀態</label><select className="w-full border border-gray-200 p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={inquiryFormData.status} onChange={e => setInquiryFormData({...inquiryFormData, status: e.target.value})}><option value="未處理">未處理</option><option value="處理中">處理中</option><option value="已回覆">已回覆</option></select></div>
              <div><label className="block text-sm font-bold text-gray-700 mb-1">處理 / 回覆備註</label><textarea rows="4" className="w-full border border-gray-200 p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none" placeholder="例如：已於 4/1 下午致電報價、安排下週場勘..." value={inquiryFormData.replyNote} onChange={e => setInquiryFormData({...inquiryFormData, replyNote: e.target.value})} /></div>
              <div className="flex justify-end space-x-3 pt-2"><button type="button" onClick={() => setEditInquiryTarget(null)} className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition">取消</button><button type="submit" className="px-5 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition flex items-center shadow-lg shadow-blue-200"><Save size={18} className="mr-2" /> 儲存紀錄</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function HomePage({ onSubmitInquiry }) {
  const scrollTo = (id) => { const element = document.getElementById(id); if (element) element.scrollIntoView({ behavior: 'smooth' }); };
  return (
    <div>
      <section id="hero" className="relative bg-blue-900 h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0"><img src={HERO_BG_SRC} alt="高陞停車場公司外觀" className="w-full h-full object-cover object-[center_80%]" /><div className="absolute inset-0 bg-blue-900/70 mix-blend-multiply"></div></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-white w-full"><h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight drop-shadow-lg tracking-wide">專業守護，<br/>讓每次停放都安心</h2>
          <p className="text-xl md:text-2xl mb-10 text-blue-50 max-w-2xl drop-shadow-md leading-relaxed">擁有超過20年機械車位保養經驗，<br className="hidden md:block"/>服務超過5000台車位。</p>
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4"><button onClick={() => scrollTo('services')} className="bg-blue-500 hover:bg-blue-400 text-white px-8 py-3.5 rounded-lg font-bold text-lg shadow-xl hover:shadow-blue-500/50">了解服務項目</button><button onClick={() => scrollTo('contact')} className="bg-transparent border-2 border-white hover:bg-white hover:text-blue-900 text-white px-8 py-3.5 rounded-lg font-bold text-lg shadow-lg">立即聯絡我們</button></div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4"><div className="text-center mb-16"><h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">為什麼選擇高陞？</h3><div className="w-24 h-1.5 bg-blue-600 mx-auto rounded-full"></div></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <FeatureCard icon={<Clock className="w-14 h-14 text-blue-600" />} title="24小時緊急待命" desc="全年無休的緊急救援服務，新竹地區平均抵達時間小於45分鐘，絕不讓您受困。" />
            <FeatureCard icon={<Shield className="w-14 h-14 text-blue-600" />} title="維修經驗豐富" desc="專精各大廠牌設備，具備精準快速的故障排除能力，為您提供最專業、具成本效益的維修與零件升級方案。" />
            <FeatureCard icon={<FileText className="w-14 h-14 text-blue-600" />} title="雲端履歷系統" desc="領先業界導入雲端管理，客戶可隨時登入查看歷史保養紀錄、現場照片，資訊100%透明化。" />
          </div>
        </div>
      </section>

      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative"><div className="absolute inset-0 bg-blue-600 rounded-2xl transform translate-x-4 translate-y-4 opacity-20"></div><img src={ABOUT_IMG_SRC} alt="機械停車設備" className="rounded-2xl shadow-2xl relative z-10 w-full object-cover h-[400px]" /></div>
            <div className="order-1 lg:order-2"><h3 className="text-sm font-bold text-blue-600 tracking-widest uppercase mb-2">About Us</h3><h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">值得信賴的機械停車專家</h2><p className="text-gray-600 mb-6 leading-relaxed text-lg">高陞停車場設備有限公司成立於2002年，致力於提升台灣機械停車設備的安全性與耐用度。我們深知機械車位是現代都市不可或缺的一部分，而<strong className="text-blue-900">「安全，是我們唯一的底線」</strong>。</p>
              <div className="bg-white p-6 rounded-xl border-l-4 border-blue-600 shadow-sm"><h4 className="font-bold text-gray-900 mb-4 flex items-center"><Shield className="mr-2 text-blue-600" size={20}/> 核心資質保證</h4><ul className="space-y-3">
                <li className="flex items-start text-gray-700"><CheckCircle size={18} className="text-green-500 mr-3 mt-0.5 flex-shrink-0"/> <span>內政部營建署核發之<strong>升降設備專業廠商登記證</strong></span></li><li className="flex items-start text-gray-700"><CheckCircle size={18} className="text-green-500 mr-3 mt-0.5 flex-shrink-0"/> <span>全系列保養合約皆投保<strong>5000萬產品責任險</strong></span></li>
              </ul></div>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16"><h3 className="text-sm font-bold text-blue-600 tracking-widest uppercase mb-2">Our Services</h3><h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">全方位設備維護方案</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 rounded-2xl p-8 hover:-translate-y-2 transition duration-300 border border-gray-100 hover:shadow-xl group"><div className="w-16 h-16 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-blue-600 transition"><Wrench className="text-blue-600 w-8 h-8 group-hover:text-white" /></div><h3 className="text-xl font-bold text-gray-900 mb-3">定期保養契約</h3><p className="text-gray-600 mb-6 text-sm">預防勝於治療。透過每月或每季的固定巡檢，包含各部傳動組件上油、感應器清潔、微動開關測試等，有效降低突發故障率。</p></div>
            <div className="bg-gray-50 rounded-2xl p-8 hover:-translate-y-2 transition duration-300 border border-gray-100 hover:shadow-xl group"><div className="w-16 h-16 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-blue-600 transition"><Settings className="text-blue-600 w-8 h-8 group-hover:text-white" /></div><h3 className="text-xl font-bold text-gray-900 mb-3">零件維修與大修升級</h3><p className="text-gray-600 mb-6 text-sm">針對使用超過 10 年的老舊設備，提供核心動力元件的更新方案，解決承載無力、混雜異音等問題。</p></div>
            <div className="bg-gray-50 rounded-2xl p-8 hover:-translate-y-2 transition duration-300 border border-gray-100 hover:shadow-xl group"><div className="w-16 h-16 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-blue-600 transition"><FileText className="text-blue-600 w-8 h-8 group-hover:text-white" /></div><h3 className="text-xl font-bold text-gray-900 mb-3">安全檢查與許可證檢查申請</h3><p className="text-gray-600 mb-6 text-sm">機械車位依法需定期進行安全檢查。我們提供從檢查，缺失改善到申報的一條龍服務，免於受罰。</p></div>
          </div>
        </div>
      </section>

      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16"><h3 className="text-sm font-bold text-blue-600 tracking-widest uppercase mb-2">Contact Us</h3><h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">聯絡高陞，獲取免費保養評估</h2></div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2 space-y-6"><div className="bg-blue-50 p-8 rounded-2xl border border-blue-100 h-full flex flex-col justify-center"><h3 className="text-2xl font-bold text-blue-900 mb-8">直接聯繫方式</h3>
                <div className="space-y-8">
                  <div className="flex items-start"><div className="bg-white p-4 rounded-xl mr-5 text-blue-600"><Phone size={24} /></div><div><p className="text-sm font-bold text-blue-900 mb-1">免付費專線</p><p className="text-2xl font-black text-gray-900">0800-212-342</p></div></div>
                  <div className="flex items-start"><div className="bg-white p-4 rounded-xl mr-5 text-red-500"><Mail size={24} /></div><div><p className="text-sm font-bold text-blue-900 mb-1">Email</p><p className="text-xl font-bold text-gray-900">ks@ks24h.com.tw</p></div></div>
                </div>
            </div></div>
            <div className="lg:col-span-3"><div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border"><ContactForm onSubmit={onSubmitInquiry} /></div></div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [currentUser, setCurrentUser] = useState(null); 
  const [dbUser, setDbUser] = useState(null); 
  const [users, setUsers] = useState([]);
  const [records, setRecords] = useState([]);
  const [inquiries, setInquiries] = useState([]); 
  const [remittances, setRemittances] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, recordId: null });
  const [detailModal, setDetailModal] = useState({ isOpen: false, record: null });
  const [editRecord, setEditRecord] = useState(null); 

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#remittance') {
        setCurrentPage('remittance');
      }
    };
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (detailModal.isOpen || editRecord !== null || deleteModal.isOpen || isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [detailModal.isOpen, editRecord, deleteModal.isOpen, isMenuOpen]);

  useEffect(() => {
    if (!auth) { setIsLoading(false); return; }
    const initAuth = async () => { try { await signInAnonymously(auth); } catch (e) { console.error(e); setIsLoading(false); } };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => setDbUser(user));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!dbUser || !db) { setIsLoading(false); return; }

    const unsubUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
      const loadedUsers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(loadedUsers);
      if (loadedUsers.length === 0) seedDatabase(); else setIsLoading(false);
    }, (error) => { console.error(error); setIsLoading(false); });

    const unsubRecords = onSnapshot(collection(db, 'records'), (snapshot) => {
      setRecords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => new Date(b.date) - new Date(a.date)));
    }, (e) => console.error(e));

    const unsubInquiries = onSnapshot(collection(db, 'inquiries'), (snapshot) => {
      setInquiries(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => b.timestamp - a.timestamp));
    }, (e) => console.error(e));

    const unsubRemittances = onSnapshot(collection(db, 'remittances'), (snapshot) => {
      setRemittances(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => b.timestamp - a.timestamp));
    }, (e) => console.error(e));

    return () => { unsubUsers(); unsubRecords(); unsubInquiries(); unsubRemittances(); };
  }, [dbUser]);

  const seedDatabase = async () => {
    if(!db) return;
    try { for (const u of SEED_USERS) await addDoc(collection(db, 'users'), u); setIsLoading(false); } 
    catch (e) { console.error(e); setIsLoading(false); }
  };

  const handleAppLogin = (username, password) => {
    const foundUser = users.find(u => u.username === username && u.password === password);
    if (foundUser) { setCurrentUser(foundUser); setCurrentPage('dashboard'); return true; }
    return false;
  };

  const handleCreateInquiry = async (inquiryData) => {
    if (!db) return { ok: false, error: '資料庫尚未連線' };
    try {
      await addDoc(collection(db, 'inquiries'), { ...inquiryData, timestamp: Date.now(), date: new Date().toLocaleDateString('zh-TW'), status: '未處理', replyNote: '' });
      return { ok: true };
    } catch (e) { return { ok: false, error: e.message }; }
  };

  const handleCreateRemittance = async (data, file) => {
    if (!db) return { ok: false, error: '資料庫尚未連線' };
    try {
      let fileUrl = '';
      if (file) {
        const fileName = `remittances/quote_${Date.now()}_${file.name}`;
        const fileRef = ref(storage, fileName);
        await uploadBytes(fileRef, file);
        fileUrl = await getDownloadURL(fileRef);
      }

      await addDoc(collection(db, 'remittances'), {
        ...data, 
        fileUrl,
        timestamp: Date.now(), 
        date: new Date().toLocaleDateString('zh-TW'), 
        status: '未對帳'
      });
      return { ok: true };
    } catch (e) { return { ok: false, error: e.message }; }
  };

  const handleUpdateRemittance = async (id, updatedData) => {
    if (!dbUser || !db) return;
    try { await updateDoc(doc(db, 'remittances', id), updatedData); window.alert("對帳狀態已更新！"); } 
    catch (e) { window.alert("更新失敗：" + e.message); }
  };

  const handleDeleteRemittance = async (id) => {
    if (!dbUser || !db) return;
    if (window.confirm("確定要刪除這筆回報紀錄嗎？")) {
      try { await deleteDoc(doc(db, 'remittances', id)); window.alert("紀錄已刪除！"); } 
      catch (e) { window.alert("刪除失敗：" + e.message); }
    }
  };

  const handleUpdateInquiry = async (inquiryId, updatedData) => {
    if (!dbUser || !db) return;
    try { await updateDoc(doc(db, 'inquiries', inquiryId), updatedData); window.alert("線上諮詢狀態更新成功！"); } 
    catch (e) { window.alert("更新失敗：" + e.message); }
  };

  const handleDeleteInquiry = async (inquiryId) => {
    if (!dbUser || !db) return;
    if (window.confirm("確定要刪除這筆諮詢紀錄嗎？")) {
      try { await deleteDoc(doc(db, 'inquiries', inquiryId)); window.alert("諮詢紀錄已成功刪除！"); } 
      catch (e) { window.alert("刪除失敗：" + e.message); }
    }
  };

  const handleAddRecord = async (newRecord) => {
    if (!dbUser || !db) return false;
    try { const { id, ...recordData } = newRecord; await addDoc(collection(db, 'records'), recordData); return true; } 
    catch (e) { window.alert("新增保養紀錄失敗：" + e.message); return false; }
  };

  const handleUpdateRecord = async (recordId, updatedData) => {
    if (!dbUser || !db) return false;
    try { await updateDoc(doc(db, 'records', recordId), updatedData); window.alert("保養紀錄更新成功！"); return true; } 
    catch (e) { window.alert("保養紀錄更新失敗：" + e.message); return false; }
  };

  const executeDeleteRecord = async () => {
    if (!dbUser || !deleteModal.recordId || !db) return;
    try {
      await deleteDoc(doc(db, 'records', deleteModal.recordId));
      setDeleteModal({ isOpen: false, recordId: null });
      setDetailModal({ isOpen: false, record: null }); 
      window.alert("保養紀錄已成功刪除！"); 
    } catch (e) { window.alert("刪除紀錄失敗：" + e.message); }
  };

  const promptDeleteRecord = (recordId) => setDeleteModal({ isOpen: true, recordId });
  const openDetailModal = (record) => setDetailModal({ isOpen: true, record });

  const handleAddUser = async (newUser) => {
    if (!dbUser || !db) return;
    try { await addDoc(collection(db, 'users'), newUser); } catch (e) { window.alert("新增帳號失敗：" + e.message); }
  };
  const handleUpdateUser = async (userId, updatedData) => {
    if (!dbUser || !db) return;
    try { await updateDoc(doc(db, 'users', userId), updatedData); window.alert("帳號資料修改成功！"); } catch (e) { window.alert("修改帳號失敗：" + e.message); }
  };
  const handleDeleteUser = async (userId) => {
    if (!dbUser || !db) return;
    const targetUser = users.find(u => u.id === userId);
    if (targetUser && targetUser.username === 'ks24h') return window.alert("無法刪除系統預設之總管理員帳號！");
    if (window.confirm("確定要刪除此帳號嗎？")) {
      try { await deleteDoc(doc(db, 'users', userId)); window.alert("帳號已成功刪除！"); } catch (e) { window.alert("帳號刪除失敗：" + e.message); }
    }
  };

  const scrollToSection = (id) => {
    if (window.location.hash) window.history.pushState(null, '', '/'); 
    if (currentPage !== 'home') setCurrentPage('home');
    setIsMenuOpen(false);
    setTimeout(() => { const element = document.getElementById(id); if (element) element.scrollIntoView({ behavior: 'smooth' }); }, 100); 
  };

  const renderPage = () => {
    if (isLoading) return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader className="w-12 h-12 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-500">正在連線至系統...</p>
      </div>
    );

    switch (currentPage) {
      case 'home': return <HomePage onSubmitInquiry={handleCreateInquiry} />;
      case 'remittance': return <RemittancePage onSubmit={handleCreateRemittance} />; 
      case 'login': return <LoginPage onLogin={handleAppLogin} />;
      case 'dashboard': 
        return <Dashboard 
          user={currentUser} records={records} users={users} inquiries={inquiries} remittances={remittances}
          onAddRecord={handleAddRecord} onUpdateRecord={handleUpdateRecord} 
          onAddUser={handleAddUser} onUpdateUser={handleUpdateUser} onDeleteUser={handleDeleteUser}
          onDeleteRecord={promptDeleteRecord} onViewDetail={openDetailModal} onEditRecord={(record) => setEditRecord(record)} 
          onUpdateInquiry={handleUpdateInquiry} onDeleteInquiry={handleDeleteInquiry}
          onUpdateRemittance={handleUpdateRemittance} onDeleteRemittance={handleDeleteRemittance}
        />;
      default: return <HomePage onSubmitInquiry={handleCreateInquiry} />;
    }
  };

  const detailDocs = detailModal.record?.documentUrls || [];
  const detailPhotos = detailModal.record?.photoUrls || (detailModal.record?.photoUrl ? [detailModal.record.photoUrl] : []);

  return (
    <div className="min-h-screen font-sans text-gray-700 bg-gray-50 flex flex-col relative">
      <nav className="bg-blue-900 text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('hero')}>
              <div className="mr-3"><CompanyLogo /></div>
              <div><h1 className="text-lg md:text-xl font-bold tracking-wider">高陞停車場設備有限公司</h1></div>
            </div>

            <div className="hidden md:flex space-x-6 items-center">
              <NavButton onClick={() => scrollToSection('hero')}>首頁</NavButton>
              <NavButton onClick={() => scrollToSection('about')}>關於我們</NavButton>
              <NavButton onClick={() => scrollToSection('services')}>服務項目</NavButton>
              <NavButton onClick={() => scrollToSection('contact')}>聯絡我們</NavButton>
              <button onClick={() => setCurrentPage('remittance')} className="text-orange-300 font-bold hover:text-white transition px-3 py-2 flex items-center">
                <FileCheck size={18} className="mr-1"/>報價回簽
              </button>
              
              {currentUser ? (
                <button onClick={() => setCurrentPage('dashboard')} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md flex items-center transition">
                  <User size={16} className="mr-2" /> {currentUser.role === 'client' ? '會員專區' : currentUser.role === 'staff' ? '員工專區' : '管理後台'}
                </button>
              ) : (
                <button onClick={() => setCurrentPage('login')} className="bg-white text-blue-900 hover:bg-blue-50 px-4 py-2 rounded-md font-medium transition">客戶登入</button>
              )}
            </div>

            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white focus:outline-none">
                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-blue-800 pb-4 absolute w-full z-40 shadow-xl">
            <MobileNavButton onClick={() => scrollToSection('hero')}>首頁</MobileNavButton>
            <MobileNavButton onClick={() => scrollToSection('about')}>關於我們</MobileNavButton>
            <MobileNavButton onClick={() => scrollToSection('services')}>服務項目</MobileNavButton>
            <MobileNavButton onClick={() => scrollToSection('contact')}>聯絡我們</MobileNavButton>
            <MobileNavButton onClick={() => { setCurrentPage('remittance'); setIsMenuOpen(false); }}>
               <span className="text-orange-300 font-bold flex items-center"><FileCheck size={18} className="mr-2"/>報價回簽</span>
            </MobileNavButton>
            <MobileNavButton onClick={() => { setCurrentPage(currentUser ? 'dashboard' : 'login'); setIsMenuOpen(false); }}>
              {currentUser ? (currentUser.role === 'client' ? '會員專區' : '管理/員工專區') : '客戶登入'}
            </MobileNavButton>
          </div>
        )}
      </nav>

      <main className="flex-grow">{renderPage()}</main>

      <footer className="bg-gray-800 text-gray-300 py-10">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-white text-lg font-bold mb-4 flex items-center"><Shield className="mr-2" /> 高陞停車場設備有限公司</h3>
            <p className="text-sm leading-relaxed">致力於提供最安全、最穩定的機械車位維護服務。24小時待命，守護您的停車安全。</p>
          </div>
          <div>
            <h4 className="text-white font-bold mb-4">聯絡資訊</h4>
            <div className="space-y-2 text-sm">
              <p className="flex items-center"><Phone size={16} className="mr-2" /> 0800-212-342</p>
              <p className="flex items-center"><Mail size={16} className="mr-2" /> ks@ks24h.com.tw</p>
              <p className="flex items-center"><MapPin size={16} className="mr-2" /> 新竹市東區中華路一段342巷15弄8號</p>
            </div>
          </div>
          <div>
            <h4 className="text-white font-bold mb-4">服務項目</h4>
            <ul className="space-y-2 text-sm">
              <li>• 定期保養檢修</li><li>• 24H 緊急故障排除</li><li>• 零件更換與升級</li><li>• 服務客戶許可證檢查申請</li>
            </ul>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-gray-700 text-center text-xs">
          &copy; {new Date().getFullYear()} GaoSheng Parking Equipment Co., Ltd. All rights reserved.
        </div>
      </footer>

      {/* 刪除確認 Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteModal({isOpen: false, recordId: null})}></div>
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 relative z-10">
            <div className="flex items-center text-red-600 mb-4"><div className="bg-red-100 p-3 rounded-full mr-3"><AlertTriangle size={24} /></div><h3 className="text-xl font-bold">確認刪除？</h3></div>
            <p className="text-gray-600 mb-6 leading-relaxed">您確定要刪除這筆紀錄嗎？<br/><span className="text-sm text-red-500 font-medium">此動作無法復原。</span></p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setDeleteModal({isOpen: false, recordId: null})} className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition">取消</button>
              <button onClick={executeDeleteRecord} className="px-5 py-2.5 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition flex items-center"><Trash2 size={18} className="mr-2" />確認刪除</button>
            </div>
          </div>
        </div>
      )}

      {/* 觀看詳細紀錄 Modal */}
      {detailModal.isOpen && detailModal.record && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDetailModal({isOpen: false, record: null})}></div>
          <div className="bg-white sm:rounded-2xl shadow-2xl w-full max-w-5xl h-[92vh] sm:h-auto sm:max-h-[90vh] overflow-hidden relative z-10 flex flex-col md:flex-row">
            <div className="order-2 md:order-1 w-full md:w-3/5 bg-gray-100 h-[45vh] md:h-[90vh] flex-shrink-0 overflow-y-auto p-4 md:p-6 custom-scrollbar border-t md:border-t-0 md:border-r border-gray-200 overscroll-contain">
              {detailDocs.length === 0 && detailPhotos.length === 0 && (<div className="flex flex-col items-center justify-center h-full text-gray-400"><Camera size={48} className="mb-2 opacity-50" /><p>無上傳任何文件或照片</p></div>)}
              {detailDocs.length > 0 && (
                <div className="mb-8"><h4 className="text-sm font-bold text-gray-600 mb-3 flex items-center bg-white p-2 rounded shadow-sm w-max"><FileImage size={16} className="mr-2 text-blue-600"/> 保養單 ({detailDocs.length})</h4>
                  <div className="space-y-4">{detailDocs.map((url, i) => (<div key={i} className="bg-white p-2 rounded-xl shadow-sm border border-gray-200 group"><img src={url} alt={`保養單 ${i+1}`} className="w-full h-auto rounded-lg object-contain bg-gray-50 max-h-[500px]"/></div>))}</div>
                </div>
              )}
              {detailPhotos.length > 0 && (
                <div><h4 className="text-sm font-bold text-gray-600 mb-3 flex items-center bg-white p-2 rounded shadow-sm w-max"><Camera size={16} className="mr-2 text-orange-500"/> 現場照片 ({detailPhotos.length})</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{detailPhotos.map((url, i) => (<div key={i} className="bg-white p-2 rounded-xl shadow-sm border border-gray-200 group"><img src={url} alt={`現場照片 ${i+1}`} className="w-full h-48 sm:h-64 object-cover rounded-lg cursor-pointer hover:opacity-90 transition"/></div>))}</div>
                </div>
              )}
            </div>
            <div className="order-1 md:order-2 w-full md:w-2/5 p-6 md:p-8 flex flex-col flex-1 min-h-0 md:h-[90vh] overflow-y-auto bg-white relative overscroll-contain">
              <button onClick={() => setDetailModal({isOpen: false, record: null})} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 bg-gray-100 rounded-full p-2 hover:bg-gray-200 transition z-10"><X size={20} /></button>
              <div className="mb-6 pt-4">
                <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2"><Calendar size={14} /><span>{detailModal.record.date}</span></div>
                <h3 className="text-2xl font-black text-gray-800 mb-3 leading-tight pr-8">{users.find(u => u.id === detailModal.record.userId)?.name || '未知客戶'}</h3>
                <span className={`inline-block px-3 py-1 text-sm rounded-full font-bold ${detailModal.record.type === '定期保養' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{detailModal.record.type}</span>
              </div>
              <div className="space-y-6 flex-grow pb-8">
                <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100/50"><h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center"><User size={16} className="mr-2 text-blue-500" /> 執行技師</h4><p className="text-lg text-gray-800 font-medium pl-6">{detailModal.record.technician}</p></div>
                <div>
                  <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center"><Wrench size={16} className="mr-2 text-gray-600" /> 施作項目詳細</h4>
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-100"><ul className="space-y-3">{detailModal.record.items.map((item, idx) => (<li key={idx} className="flex items-start text-gray-700"><CheckCircle size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" /><span className="leading-relaxed">{item}</span></li>))}</ul></div>
                </div>
              </div>
              {currentUser?.role !== 'client' && (
                <div className="mt-4 pt-4 border-t border-gray-100 sticky bottom-0 bg-white pb-2">
                  <button onClick={() => promptDeleteRecord(detailModal.record.id)} className="w-full flex items-center justify-center text-red-500 font-medium px-4 py-3 bg-red-50 hover:bg-red-100 rounded-xl transition border border-red-100"><Trash2 size={18} className="mr-2" /> 刪除此紀錄</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 編輯保養紀錄 Modal */}
      {editRecord && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setEditRecord(null)}></div>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl relative z-10 max-h-[90vh] overflow-y-auto custom-scrollbar overscroll-contain">
            <button onClick={() => setEditRecord(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-gray-100 p-2 rounded-full z-20"><X size={20} /></button>
            <AddRecordForm users={users} initialData={editRecord} onAdd={async (data) => await handleUpdateRecord(editRecord.id, data)} onCancel={() => setEditRecord(null)} isEditing={true} />
          </div>
        </div>
      )}
    </div>
  );
}